'use client';

import { motion } from 'framer-motion';
import { PortableText } from 'next-sanity';
import Image from 'next/image';

import {
  FEATURED_PRIZE,
  Prize,
  SECONDARY_PRIZES,
} from '@/lib/prize-wheel/prizes';
import { richTextComponent } from '@/sanity/sanityPortableText';
import cardDots from '@/public/prize-wheel/card-dots.png';

import type { PortableTextBlock } from 'sanity';

const ease = [0.25, 0.1, 0.25, 1] as const;

/** From the comp: the featured card is the same green as its wheel segment. */
const CARD_GREEN = '#307F6A';
const BAR_GREEN = '#228E71';

export interface GuaranteedPrizesData {
  prizesChip?: string;
  prizesTitle?: PortableTextBlock[];
  prizesLead?: PortableTextBlock[];
  chanceLabel?: string;
}

/**
 * The dot texture every card carries in the comp ("image 419"): a 562×578 box
 * centred on the card, turned 90°, exclusion-blended at 10%. On white it reads
 * as faint dark dots, on the green card as faint light ones.
 */
function CardTexture() {
  return (
    <Image
      src={cardDots}
      alt=""
      aria-hidden="true"
      className="pointer-events-none absolute left-1/2 top-1/2 h-[72.25rem] w-[70.25rem] max-w-none -translate-x-1/2 -translate-y-1/2 rotate-90 object-cover opacity-10 mix-blend-exclusion"
    />
  );
}

function ChanceBar({
  prize,
  label,
  onGreen,
}: {
  prize: Prize;
  label: string;
  onGreen?: boolean;
}) {
  return (
    <div
      className={`flex h-[6rem] w-full items-start justify-between gap-[2rem] rounded-[1rem] border px-[2.375rem] pb-[2rem] pt-[1.75rem] font-geist-mono font-medium uppercase leading-none text-white ${
        onGreen
          ? 'border-white/10 bg-white/10 backdrop-blur-[1.1rem]'
          : 'border-[rgba(34,142,113,0.18)]'
      }`}
      style={onGreen ? undefined : { background: BAR_GREEN }}
    >
      <span className="font-geist-mono text-[1.75rem] tracking-[-0.0525rem] lg:text-[2rem] lg:tracking-[-0.06rem]">{label}</span>
      <span className="font-geist-mono text-[1.75rem] tracking-[-0.0525rem] [font-variant-numeric:tabular-nums]">{prize.weight}%</span>
    </div>
  );
}

export default function GuaranteedPrizesSection({
  data,
}: {
  data: GuaranteedPrizesData;
}) {
  const chanceLabel = data.chanceLabel ?? 'CHANCE TO WIN';

  return (
    <section className="flex w-full flex-col items-center bg-[#F7F7F7] font-geist max-lg:px-[2rem] max-lg:py-[8rem] lg:px-[6rem] lg:py-[15rem]">
      <div className="flex w-[min(170.25rem,100%)] flex-col items-center max-lg:gap-[2.5rem] lg:gap-[5.5rem]">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8, ease }}
          className="flex flex-col items-center max-lg:gap-[1.25rem] lg:gap-0"
        >
          {data.prizesChip && (
            <p className="mb-0 font-blacker text-[2rem] italic leading-[1.5] text-black">
              {data.prizesChip}
            </p>
          )}
          {data.prizesTitle && (
            <div className="richtext flex flex-col items-center text-center prose-h2:mb-0 prose-h2:font-medium prose-h2:leading-[1.15] max-lg:prose-h2:text-[4.5rem] max-lg:prose-h2:tracking-[-0.225rem] lg:prose-h2:text-[6rem] lg:prose-h2:tracking-[-0.3rem]">
              <PortableText value={data.prizesTitle} components={richTextComponent} />
            </div>
          )}
          {data.prizesLead && (
            <div className="richtext text-center prose-p:mb-0 prose-p:[text-wrap:wrap] prose-p:text-[2.25rem] prose-p:leading-[1.5] prose-p:text-black/[0.7] max-lg:w-[min(43rem,100%)] lg:mt-[1.75rem] lg:w-[min(63rem,100%)] lg:prose-p:font-medium">
              <PortableText value={data.prizesLead} components={richTextComponent} />
            </div>
          )}
        </motion.div>

        <div className="grid w-full grid-cols-1 max-lg:gap-[2.5rem] lg:grid-cols-3 lg:grid-rows-2 lg:gap-[3rem]">
          <motion.article
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.8, ease }}
            className="relative isolate flex flex-col justify-between overflow-hidden rounded-[1rem] text-white max-lg:min-h-[59.375rem] max-lg:p-[3rem] lg:row-span-2 lg:min-h-[65.625rem] lg:p-[5.5rem]"
            style={{ background: CARD_GREEN }}
          >
            <Image
              src="/prize-wheel/statue-100k-funded-card.png"
              alt=""
              aria-hidden="true"
              width={1200}
              height={896}
              sizes="(max-width: 992px) 160vw, 673px"
              quality={100}
              className="pointer-events-none absolute left-[1.375rem] top-[12.125rem] h-[65.25rem] w-[87.5rem] max-w-none -scale-x-100 object-cover mix-blend-luminosity"
            />
            <CardTexture />

            <div className="relative flex w-[min(43.75rem,100%)] flex-col max-lg:gap-[1.25rem] lg:gap-[1.5rem]">
              <div className="flex flex-col gap-[1.75rem]">
                <p className="mb-0 font-blacker text-[2rem] italic leading-[1.5]">
                  {FEATURED_PRIZE.cardLabel}
                </p>
                <h3 className="mb-0 text-[4.5rem] font-medium leading-[1.15] tracking-[-0.225rem]">
                  {FEATURED_PRIZE.cardName}
                </h3>
              </div>
              <p className="mb-0 whitespace-pre-line opacity-60 [text-wrap:wrap] max-lg:w-[min(28.625rem,100%)] max-lg:text-[2rem] max-lg:tracking-[-0.1rem] lg:w-[min(31.875rem,100%)] lg:text-[2.25rem] lg:tracking-[-0.1125rem] leading-[1.5]">
                {FEATURED_PRIZE.cardDescription}
              </p>
            </div>

            <div className="relative">
              <ChanceBar prize={FEATURED_PRIZE} label={chanceLabel} onGreen />
            </div>
          </motion.article>

          {SECONDARY_PRIZES.map((prize, i) => (
            <motion.article
              key={prize.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.8, ease, delay: 0.08 * (i + 1) }}
              className="relative isolate flex min-h-[31.375rem] flex-col justify-between gap-[3rem] overflow-hidden rounded-[1rem] border border-black/10 bg-white p-[4rem] text-black"
            >
              <CardTexture />

              <div className="relative flex flex-col gap-[1.75rem]">
                <div className="flex flex-col gap-[1.75rem]">
                  <p className="mb-0 font-blacker text-[2rem] italic leading-[1.5]">
                    {prize.cardLabel}
                  </p>
                  <h3 className="mb-0 text-[3.5rem] font-medium leading-[1.15] tracking-[-0.175rem]">
                    {prize.cardName}
                  </h3>
                </div>
                <p className="mb-0 leading-[1.5] opacity-60 [text-wrap:wrap] max-lg:text-[2rem] max-lg:tracking-[-0.1rem] lg:text-[2.25rem] lg:tracking-[-0.1125rem]">
                  {prize.cardDescription}
                </p>
              </div>

              <div className="relative">
                <ChanceBar prize={prize} label={chanceLabel} />
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
